import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

import type { BookableService } from "../api/journal-actions.api"
import { cx } from "../lib/cx"
import { noShowLabel, visitsLabel } from "../lib/plural"
import { Button, IconButton } from "../ui/button"
import { Checkbox, SelectField, TextField } from "../ui/field"
import { initialsOf } from "../theme/master-color"
import type { BookingEvent, BookingState, FreeWindow } from "./booking-draft"
import type { BookingDayWindows } from "../screens/journal/use-booking-session"
import { canSubmit, CONSENT_PERSONAL_DATA_LABEL } from "./booking-draft"
import type { BookingStep } from "./booking-steps"
import {
  BOOKING_STEP_COUNT,
  stepBlocker,
  stepLabel,
  stepSubmitLabel,
  servicesSummary,
  toggleService,
  totalPriceMinor,
} from "./booking-steps"
import styles from "./journal-actions.module.css"
import "./journal-tokens.css"
import { formatCountdown, formatDuration, formatMinute, formatRange } from "./journal-time"
import { shiftDate } from "../lib/format"
import { formatMoney } from "./money"

/**
 * Быстрая форма записи 2-2.7.1 и главный сценарий листа — 2-2.10.2 «слот заняли».
 *
 * ФОРМА НЕ ХРАНИТ НИ ОДНОГО ПОЛЯ. Всё, что ввёл человек, лежит в BookingState выше
 * (см. booking-draft.ts), и это не архитектурная прихоть: состояние, живущее внутри
 * компонента, умирает вместе с ним — при перерисовке после отказа, при смене дня,
 * при любом ключе, который кто-нибудь однажды добавит в разметку. Обещание «введённое
 * не теряется» может держать только то состояние, которое переживает форму.
 *
 * МИНИМУМ ПОЛЕЙ, И КАЖДОЕ ОБОСНОВАНО (ТЗ §2.7.1):
 *   телефон — по нему находится или заводится клиент, другого ключа у стойки нет;
 *   имя и два согласия — только для нового клиента и только потому, что без согласия
 *     на обработку данных карточку создать нельзя юридически;
 *   услуга — от неё зависят длительность и цена, то есть сам слот;
 *   мастер и время — подставлены из места клика, но остаются исправимыми: клиент
 *     в трубке говорит «а можно на полчаса позже»;
 *   комментарий — необязателен и стоит последним.
 *
 * Чего в форме НЕТ: продолжительности (её знает услуга), суммы (её считает сервер),
 * статуса (новая запись не бывает завершённой) и филиала (он в адресе журнала).
 */

export interface BookingFormCallbacks {
  readonly onClose: () => void
  /** notify=false — «Записать», notify=true — «Записать и отправить подтверждение». */
  readonly onSubmit: (notifyClient: boolean) => void
  readonly onPickWindow: (window: FreeWindow) => void
  readonly onRetryWindows: (all: boolean) => void
  /**
   * «Придержать время»: закрыть форму, не отпуская слот (2-2.4.1). null — удерживать
   * нечего: права нет, удержание не встало или уже истекло.
   */
  readonly onKeepHold: (() => void) | null
  /** Перечитать день после истёкшего удержания: «Обновить» из макета. */
  readonly onRefresh: () => void
  /** Показать свободные окна этого дня (шаг «Время» на телефоне). */
  readonly onPickDay: (date: string) => void
  /** Шаг сменился: состояние шага живёт снаружи, как и черновик. */
  readonly onStep: (step: BookingStep) => void
}

export function BookingForm(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  /** Услуги ВЫБРАННОГО мастера: чужие в этой форме не предлагаются. */
  readonly services: readonly BookableService[]
  readonly employees: readonly { readonly id: string; readonly displayName: string }[]
  readonly employeeName: string
  /** Каталог не загрузился: записать нельзя, и это надо сказать, а не молчать. */
  readonly catalogFailed: boolean
  /**
   * Каталог ещё едет. Отдельно от «услуг нет», потому что пустой список во время
   * загрузки неотличим от пустого каталога — и форма успевала посоветовать пойти
   * отметить услуги мастеру, у которого они отмечены. Совет, данный по незнанию,
   * администратор выполняет.
   */
  readonly catalogLoading: boolean
  /**
   * Мастер не выбран («Без мастера»). Тогда показываются ВСЕ услуги филиала,
   * и подпись про «услуги этого мастера» становится неправдой.
   */
  readonly forAnyMaster: boolean
  /** Секунд до конца удержания. null — удержания нет (нет права holds.manage). */
  readonly holdSecondsLeft: number | null
  /**
   * Подпись карантинного времени: «Время за салоном до 19:40». Отдельно от отсчёта
   * удержания, потому что это ДРУГОЕ обещание: карантин держит сервер и держит его
   * от онлайн-записи, а не от коллеги за соседней стойкой. null — время обычное.
   */
  readonly quarantineNote: string | null
  /** Срок удержания из правил салона — в объяснении истёкшего. null: число не выдумываем. */
  readonly holdTtlSeconds: number | null
  readonly canEdit: boolean
  readonly deniedReason: string | null
  /** Положение поповера в рабочей области, из макета: overlayTop + 68 и left 80. */
  /**
   * Телефонный режим: форма показывает ОДИН шаг из пяти (5-А2).
   *
   * Решение принимает вызывающий, а не форма: порог ширины — свойство оболочки,
   * и два места, каждое со своим matchMedia, однажды разойдутся.
   */
  readonly steps: boolean
  readonly step: BookingStep
  /** Свободные окна выбранного дня. null — день ещё не спрашивали. */
  readonly dayWindows: BookingDayWindows | null
  /** Сегодняшняя дата филиала — от неё считаются чипы «Сегодня» и «Завтра». */
  readonly today: string
  /** Дата согласия на обработку данных: показывается на шаге «Итог». */
  readonly consentGrantedAt: string | null
  readonly top: number
  readonly left: number
  readonly callbacks: BookingFormCallbacks
}): ReactNode {
  const { state, dispatch, callbacks } = props
  const { draft } = state
  const masterBoxRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)

  // Escape сворачивает форму. Введённое при этом ОСТАЁТСЯ: адрес слота уходит из строки
  // запроса, а черновик живёт выше по дереву и возвращается при повторном клике в то же
  // место. Это ровно то, что обещает подпись в подвале формы.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      callbacks.onClose()
    }
    const node = formRef.current
    node?.addEventListener("keydown", onKeyDown)
    return () => {
      node?.removeEventListener("keydown", onKeyDown)
    }
  }, [callbacks])

  const endMinute = draft.startMinute + draft.durationMinutes
  /*
   * НЕРАЗОБРАННОЕ ПРЕДУПРЕЖДЕНИЕ ЗАКРЫВАЕТ ОБЫЧНУЮ КНОПКУ ЗАПИСИ.
   *
   * Блок предупреждения даёт два явных выхода — «Записать всё равно» и «Не
   * записывать», — но на ноутбуке внизу оставалась прежняя «Записать», и она
   * отправляла запрос мимо них. То есть ровно та ловушка, ради устранения которой
   * блок и заведён: человек жмёт привычную кнопку и узнаёт о последствии после.
   */
  const pendingWarnings = state.warnings.length > 0
  const ready = canSubmit(state) && props.canEdit && !props.catalogFailed
  const readyToSubmit = ready && !pendingWarnings

  const deniedReason = props.canEdit ? undefined : (props.deniedReason ?? undefined)

  return (
    <div
      ref={formRef}
      className={styles["form"]}
      style={{ top: `${String(props.top)}px`, left: `${String(props.left)}px` }}
      role="dialog"
      aria-label="Новая запись"
    >
      <header className={styles["formHead"]}>
        <div className={styles["formTitle"]}>Новая запись</div>
        {props.steps ? null : (
          <div className={styles["formWhere"]}>
            {props.employeeName} · {formatMinute(draft.startMinute)}
          </div>
        )}
        {/*
          * На карантинной ячейке своего удержания не будет: слот уже закрыт от онлайн-записи
          * строкой занятости карантина, и второе удержание поверх неё не встаёт. Молчать об
          * этом нельзя — отсутствие отсчёта читается как «слот ничем не защищён», и человек
          * торопится там, где торопиться не надо.
          */}
        {props.quarantineNote !== null ? (
          <span className={styles["holdChip"]}>{props.quarantineNote}</span>
        ) : props.holdSecondsLeft === null || props.holdSecondsLeft <= 0 ? null : (
          <span className={styles["holdChip"]}>
            Держим ещё {formatCountdown(props.holdSecondsLeft)}
          </span>
        )}
        <IconButton label="Закрыть форму записи" icon="✕" onClick={callbacks.onClose} />
      </header>

      {props.steps ? <StepHead step={props.step} /> : null}

      {/*
        * ИСТЁКШЕЕ УДЕРЖАНИЕ ОБЪЯСНЯЕТСЯ, А НЕ ОТМЕЧАЕТСЯ (2-2.4.1). Подпись «Удержание
        * закончилось» ничего не говорила о последствии: слот всё это время могли занять,
        * и форма продолжала выглядеть рабочей. Текст называет, сколько слот держался
        * и что теперь проверить, а «Обновить» перечитывает день на месте — без него
        * единственным выходом была перезагрузка страницы с потерей набранного.
        */}
      {props.holdSecondsLeft !== null && props.holdSecondsLeft <= 0 && props.quarantineNote === null ? (
        <div className={styles["holdLost"]} role="status">
          <span>
            Время больше не удерживается.
            {props.holdTtlSeconds === null
              ? " "
              : ` Слот держался ${formatDuration(Math.round(props.holdTtlSeconds / 60))} и был отпущен. `}
            Проверьте, свободен ли он ещё.
          </span>
          <Button variant="secondary" size="sm" onClick={callbacks.onRefresh}>
            Обновить
          </Button>
        </div>
      ) : null}

      {state.conflict === null ? null : (
        <SlotTakenBlock
          state={state}
          onPickWindow={callbacks.onPickWindow}
          onRetryWindows={callbacks.onRetryWindows}
          onOtherMaster={() => {
            // «Другой мастер» не открывает второй экран: поле мастера уже в этой форме,
            // на него достаточно перевести фокус. Меньше слоёв — меньше потерянного ввода.
            masterBoxRef.current?.querySelector("select")?.focus()
          }}
        />
      )}

      {props.steps ? (
        <StepBody
          step={props.step}
          state={state}
          dispatch={dispatch}
          services={props.services}
          employees={props.employees}
          catalogFailed={props.catalogFailed}
          catalogLoading={props.catalogLoading}
          forAnyMaster={props.forAnyMaster}
          dayWindows={props.dayWindows}
          today={props.today}
          consentGrantedAt={props.consentGrantedAt}
          ready={ready}
          deniedReason={deniedReason}
          onPickDay={callbacks.onPickDay}
          onPickWindow={callbacks.onPickWindow}
          onSubmit={callbacks.onSubmit}
          onClose={callbacks.onClose}
        />
      ) : (
        <div className={styles["formBody"]}>
        <ClientBlock state={state} dispatch={dispatch} />

        <ServicesBlock
          state={state}
          dispatch={dispatch}
          services={props.services}
          catalogFailed={props.catalogFailed}
          catalogLoading={props.catalogLoading}
          forAnyMaster={props.forAnyMaster}
        />

        <div className={styles["formRow"]}>
          <div ref={masterBoxRef}>
            <MasterField state={state} dispatch={dispatch} employees={props.employees} />
          </div>
          <TextField
            label="Время"
            type="time"
            step={300}
            value={formatMinute(draft.startMinute)}
            hint={formatRange(draft.startMinute, endMinute)}
            onChange={(event) => {
              const minutes = parseTime(event.currentTarget.value)
              if (minutes === null) return
              dispatch({ type: "start-minute", minute: minutes })
            }}
          />
        </div>

        <TextField
          label="Комментарий"
          placeholder="Необязательно"
          value={draft.comment}
          onChange={(event) => {
            dispatch({ type: "comment", value: event.currentTarget.value })
          }}
        />

        <WarningsBlock
          state={state}
          ready={ready}
          deniedReason={deniedReason}
          onConfirm={() => {
            callbacks.onSubmit(false)
          }}
          onCancel={callbacks.onClose}
        />

        {state.failure === null ? null : (
          <div className={styles["failure"]} role="alert">
            {state.failure}
          </div>
        )}
        </div>
      )}

      {props.steps ? (
        <StepFoot
          step={props.step}
          state={state}
          ready={ready}
          deniedReason={deniedReason}
          onStep={callbacks.onStep}
          onSubmit={callbacks.onSubmit}
          onClose={callbacks.onClose}
        />
      ) : (
      <footer className={styles["formFoot"]}>
        <Button
          variant="primary"
          size="sm"
          disabled={!readyToSubmit}
          loading={state.submitting}
          loadingLabel="Записываем…"
          deniedReason={
            pendingWarnings ? "Сначала решите предупреждение выше" : deniedReason
          }
          onClick={() => {
            callbacks.onSubmit(false)
          }}
        >
          Записать
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!readyToSubmit}
          deniedReason={
            pendingWarnings ? "Сначала решите предупреждение выше" : deniedReason
          }
          onClick={() => {
            callbacks.onSubmit(true)
          }}
        >
          Записать и отправить подтверждение
        </Button>
        {/*
          * «Придержать время» — отдельное решение, а не половина записи: клиент думает,
          * перезванивает через десять минут, и до его звонка слот не должен уйти
          * в онлайн-запись. Кнопки нет, когда держать нечем: обещание без удержания
          * хуже отсутствия кнопки.
          */}
        {callbacks.onKeepHold === null ? null : (
          <Button
            variant="secondary"
            size="sm"
            deniedReason={deniedReason}
            onClick={() => {
              callbacks.onKeepHold?.()
            }}
          >
            Придержать время
          </Button>
        )}
        <span className={styles["escHint"]}>
          Esc — свернуть,
          <br />
          введённое останется
        </span>
      </footer>
      )}

    </div>
  )
}

/**
 * Блок «Это время только что заняли» — 2-2.10.2.
 *
 * Стоит НАД полями и ничего в них не трогает. Четыре подсостояния отличаются только
 * тем, что известно о свободном времени; введённое сохранено во всех четырёх, и это
 * сказано человеку словами, а не подразумевается.
 */
function SlotTakenBlock(props: {
  readonly state: BookingState
  readonly onPickWindow: (window: FreeWindow) => void
  readonly onRetryWindows: (all: boolean) => void
  readonly onOtherMaster: () => void
}): ReactNode {
  const conflict = props.state.conflict
  if (conflict === null) return null
  const search = conflict.search

  return (
    <section className={styles["conflict"]} role="status" aria-live="polite">
      <div className={styles["conflictTitle"]}>Это время только что заняли</div>
      {/*
       * Имя мастера в тексте НЕ склоняется: API отдаёт одно поле displayName
       * в именительном падеже, и «у Анна Соколова» в главном сообщении экрана выглядит
       * как поломка. Мастер при этом виден рядом — в шапке формы и в поле «Мастер».
       */}
      <div className={styles["conflictText"]}>
        Пока вы оформляли запись, {conflict.takenLabel} занял другой клиент. Введённое
        сохранено — выберите другое время.
      </div>

      {search.kind === "searching" ? (
        <div className={styles["conflictRow"]}>
          {/* Каркас ровно того размера, что и будущие кнопки: блок не прыгнет. */}
          <span className={styles["slotSkeleton"]} />
          <span className={styles["slotSkeleton"]} />
          <span className={styles["slotSkeleton"]} />
          <span className={styles["conflictLabel"]}>Ищем ближайшие окна…</span>
        </div>
      ) : null}

      {search.kind === "found" ? (
        <div className={styles["conflictRow"]}>
          <span className={styles["conflictLabel"]}>Ближайшее свободное:</span>
          {search.windows.map((window, index) => (
            <Button
              key={`${window.date}-${String(window.minute)}`}
              variant={index === 0 ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                props.onPickWindow(window)
              }}
            >
              {window.label}
            </Button>
          ))}
          <button
            type="button"
            className={cx(styles["worksAction"], styles["conflictAside"])}
            onClick={props.onOtherMaster}
          >
            Другой мастер
          </button>
        </div>
      ) : null}

      {search.kind === "none" ? (
        <>
          <div className={styles["conflictText"]}>
            У этого мастера на сегодня свободного времени не осталось.
            {search.next === null
              ? " Ближайшее не нашлось — выберите другого мастера."
              : ` Ближайшее: ${search.next.label}.`}
          </div>
          <div className={styles["conflictRow"]}>
            {search.next === null ? null : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (search.next !== null) props.onPickWindow(search.next)
                }}
              >
                {search.next.label}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={props.onOtherMaster}>
              Выбрать другого мастера
            </Button>
          </div>
        </>
      ) : null}

      {search.kind === "failed" ? (
        <>
          <div className={styles["conflictFailed"]}>
            Свободное время не подгрузилось. Введённое сохранено.
          </div>
          <div className={styles["conflictRow"]}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                props.onRetryWindows(false)
              }}
            >
              Повторить
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                props.onRetryWindows(true)
              }}
            >
              Показать все окна
            </Button>
          </div>
        </>
      ) : null}
    </section>
  )
}

/** Результат поиска клиента под полем телефона: три исхода макета плюс два служебных. */
function ClientLookupResult(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
}): ReactNode {
  const lookup = props.state.client

  if (lookup.kind === "searching") {
    return <div className={styles["hint"]}>Ищем клиента…</div>
  }

  if (lookup.kind === "failed") {
    return (
      <div className={styles["hint"]}>
        {lookup.message} Введённое сохранено — можно записать как нового клиента.
      </div>
    )
  }

  if (lookup.kind === "new") {
    return (
      <div className={styles["hint"]}>Такого номера в базе нет — запишем как нового клиента</div>
    )
  }

  if (lookup.kind === "found") {
    const client = lookup.client
    return (
      <div className={styles["foundCard"]}>
        <span className={styles["foundAvatar"]} aria-hidden="true">
          {initialsOf(client.displayName)}
        </span>
        <div>
          <div className={styles["foundName"]}>{client.displayName}</div>
          <div className={styles["foundMeta"]}>
            {visitsLabel(client.visitsCount)}
            {client.noShowCount > 0 ? (
              <>
                {" · "}
                <span className={styles["foundAlarm"]}>{noShowLabel(client.noShowCount)}</span>
              </>
            ) : null}
            {client.isBlacklisted ? (
              <>
                {" · "}
                <span className={styles["foundAlarm"]}>онлайн-запись ограничена</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  if (lookup.kind === "many") {
    return (
      <>
        <div className={styles["options"]}>
          {lookup.options.map((option) => (
            <button
              key={option.id}
              type="button"
              className={styles["option"]}
              aria-pressed={props.state.draft.clientId === option.id}
              onClick={() => {
                props.dispatch({ type: "choose-client", client: option })
              }}
            >
              <span className={styles["foundName"]}>{option.displayName}</span>
              <span className={styles["optionMeta"]}>{visitsLabel(option.visitsCount)}</span>
            </button>
          ))}
        </div>
        <div className={styles["hint"]}>
          У номера несколько карточек — выберите, кого записываем
        </div>
      </>
    )
  }

  return null
}

/** «14:30» → 870. Чужое значение не двигает время: пустой ввод не должен стирать слот. */
function parseTime(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * Услуги визита — СПИСОК, а не одна.
 *
 * «Стрижка и борода» — обычный заказ у стойки, и раньше администратор заводил его
 * двумя записями подряд: клиент получал два подтверждения, салон — две строки
 * в журнале вместо одного визита на час, а время между ними оставалось «свободным»
 * и уходило в онлайн-запись.
 *
 * Кнопками, а не выпадающим списком: на телефоне список из двадцати услуг — это
 * системная шторка на пол-экрана, из которой нельзя выбрать вторую, не открыв её
 * заново. Кнопка же показывает выбранное и остаётся на месте.
 */
function ServicesBlock(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly services: readonly BookableService[]
  readonly catalogFailed: boolean
  readonly catalogLoading: boolean
  readonly forAnyMaster: boolean
}): ReactNode {
  const selected = props.state.draft.serviceIds
  const hint = props.catalogFailed
    ? "Услуги не загрузились. Повторите позже — без услуги запись создать нельзя."
    : props.catalogLoading
      ? "Загружаем услуги…"
      : props.services.length === 0
        ? props.forAnyMaster
          ? "В каталоге нет ни одной активной услуги. Заведите её в каталоге — без услуги записать нельзя."
          : "У этого мастера не отмечено ни одной услуги. Отметьте их в карточке сотрудника — без этого записать нельзя."
        : props.forAnyMaster
          ? "Мастер не выбран — показаны все услуги филиала с общей длительностью и ценой"
          : "Показаны только услуги этого мастера, с его длительностью и ценой"

  const summary = servicesSummary(selected, props.services)
  const price = totalPriceMinor(selected, props.services)

  return (
    <div className={styles["serviceBlock"]}>
      <span className={styles["fieldLabel"]}>Услуги</span>
      <div className={styles["serviceChips"]} role="group" aria-label="Услуги визита">
        {props.services.map((item) => {
          const on = selected.includes(item.id)
          return (
            <button
              key={item.id}
              type="button"
              className={cx(styles["serviceChip"], on && styles["serviceChipOn"])}
              aria-pressed={on}
              onClick={() => {
                const next = toggleService(selected, props.services, item.id)
                props.dispatch({
                  type: "services",
                  serviceIds: next.serviceIds,
                  durationMinutes: next.durationMinutes,
                })
              }}
            >
              {item.name} · {String(item.durationMinutes)} мин
              {item.priceMinor === null ? "" : ` · ${formatMoney(item.priceMinor)}`}
            </button>
          )
        })}
      </div>
      {summary === null ? null : (
        <span className={styles["serviceTotal"]}>
          {summary}
          {price === null ? "" : ` · ${formatMoney(price)}`}
        </span>
      )}
      <span className={styles["fieldHint"]}>{hint}</span>
    </div>
  )
}

/**
 * Предупреждение сервера — С ДВУМЯ ДЕЙСТВИЯМИ, а не «нажмите ещё раз».
 *
 * «Нажмите «Записать» ещё раз, чтобы подтвердить» — это ловушка: человек, читающий
 * «клиент в чёрном списке», жмёт ту же кнопку по привычке и узнаёт о последствии
 * после. Решение должно называться словами и иметь два выхода, а отказ от записи —
 * быть таким же явным действием, как согласие.
 */
function WarningsBlock(props: {
  readonly state: BookingState
  readonly ready: boolean
  readonly deniedReason: string | undefined
  readonly onConfirm: () => void
  readonly onCancel: () => void
}): ReactNode {
  if (props.state.warnings.length === 0) return null
  return (
    <div className={styles["warnings"]} role="status">
      <span className={styles["warningsTitle"]}>Проверьте перед записью</span>
      {props.state.warnings.map((warning) => (
        <span key={warning.code}>{warning.message}</span>
      ))}
      <div className={styles["warningsActions"]}>
        <Button
          variant="primary"
          size="sm"
          disabled={!props.ready}
          loading={props.state.submitting}
          loadingLabel="Записываем…"
          deniedReason={props.deniedReason}
          onClick={props.onConfirm}
        >
          Записать всё равно
        </Button>
        <Button variant="secondary" size="sm" onClick={props.onCancel}>
          Не записывать
        </Button>
      </div>
    </div>
  )
}

/**
 * Клиент: номер, результат поиска и — для нового — имя с двумя согласиями.
 *
 * Один компонент на два расположения: на ноутбуке это первый блок общей формы,
 * на телефоне — целиком первый шаг. Второй экземпляр той же разметки разошёлся бы
 * с первым на первой же правке текста согласия, а текст согласия — юридический.
 */
function ClientBlock(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
}): ReactNode {
  const { state, dispatch } = props
  const draft = state.draft
  return (
    <>
      <div>
        <TextField
          label="Телефон клиента"
          inputMode="tel"
          autoComplete="off"
          autoFocus
          value={draft.phone}
          placeholder="+7 ___ ___-__-__"
          onChange={(event) => {
            dispatch({ type: "phone", value: event.currentTarget.value })
          }}
        />
        <ClientLookupResult state={state} dispatch={dispatch} />
      </div>

      {state.client.kind === "new" ? (
        <>
          <TextField
            label="Как называть клиента"
            required
            placeholder="Имя со слов клиента"
            value={draft.clientName}
            onChange={(event) => {
              dispatch({ type: "client-name", value: event.currentTarget.value })
            }}
          />
          {/*
           * ДВА РАЗДЕЛЬНЫХ СОГЛАСИЯ. С 01.09.2025 согласие на обработку данных
           * и согласие на рекламу — разные документы; один флажок на оба означает
           * штраф салону. Поэтому здесь два независимых поля, и второе по умолчанию НЕ
           * отмечено: молчание клиента не является согласием на рассылку.
           */}
          <div className={styles["consents"]}>
            <Checkbox
              checked={draft.consentPersonalData}
              onChange={(value) => {
                dispatch({ type: "consent-personal", value })
              }}
            >
              Согласие на обработку данных получено <b>устно</b> ·{" "}
              {CONSENT_PERSONAL_DATA_LABEL}
            </Checkbox>
            <div className={styles["consentSplit"]} />
            <Checkbox
              className={styles["consentAds"]}
              checked={draft.consentAds}
              onChange={(value) => {
                dispatch({ type: "consent-ads", value })
              }}
            >
              Согласие на рекламные рассылки — отдельный документ, по умолчанию нет
            </Checkbox>
          </div>
        </>
      ) : null}
    </>
  )
}

/** Мастер: поле нужно и в строке формы, и отдельным шагом на телефоне. */
function MasterField(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly employees: readonly { readonly id: string; readonly displayName: string }[]
}): ReactNode {
  return (
    <SelectField
      label="Мастер"
      value={props.state.draft.employeeId ?? ""}
      hint="«Без мастера» — законный выбор: запись ляжет в колонку неразобранных, и её разберут потом"
      onChange={(event) => {
        const value = event.currentTarget.value
        props.dispatch({ type: "employee", employeeId: value === "" ? null : value })
      }}
      options={[
        { value: "", label: "Без мастера" },
        ...props.employees.map((item) => ({ value: item.id, label: item.displayName })),
      ]}
    />
  )
}

/**
 * Шапка шагов: «Шаг 2 из 5 · услуги» и пять полос.
 *
 * ПОЛОСЫ, А НЕ ПРОЦЕНТЫ. Человеку у стойки важно не «сколько процентов заполнено»,
 * а сколько экранов осталось до конца разговора: пять полос отвечают на это мгновенно,
 * не требуя чтения.
 */
function StepHead(props: { readonly step: BookingStep }): ReactNode {
  return (
    <div className={styles["stepHead"]}>
      <span className={styles["stepName"]}>{stepLabel(props.step)}</span>
      <div className={styles["stepDots"]} aria-hidden="true">
        {[1, 2, 3, 4, 5].map((dot) => (
          <span
            key={dot}
            className={dot <= props.step ? styles["stepDotOn"] : styles["stepDot"]}
          />
        ))}
      </div>
    </div>
  )
}

/** Один шаг из пяти. Черновик общий — шаги показывают его части, а не хранят свои. */
function StepBody(props: {
  readonly step: BookingStep
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly services: readonly BookableService[]
  readonly employees: readonly { readonly id: string; readonly displayName: string }[]
  readonly catalogFailed: boolean
  readonly catalogLoading: boolean
  readonly forAnyMaster: boolean
  readonly dayWindows: BookingDayWindows | null
  readonly today: string
  readonly consentGrantedAt: string | null
  readonly ready: boolean
  readonly deniedReason: string | undefined
  readonly onPickDay: (date: string) => void
  readonly onPickWindow: (window: FreeWindow) => void
  readonly onSubmit: (notifyClient: boolean) => void
  readonly onClose: () => void
}): ReactNode {
  const { state, dispatch } = props
  return (
    <div className={styles["formBody"]}>
      {props.step === 1 ? <ClientBlock state={state} dispatch={dispatch} /> : null}

      {props.step === 2 ? (
        <ServicesBlock
          state={state}
          dispatch={dispatch}
          services={props.services}
          catalogFailed={props.catalogFailed}
          catalogLoading={props.catalogLoading}
          forAnyMaster={props.forAnyMaster}
        />
      ) : null}

      {props.step === 3 ? (
        <MasterField state={state} dispatch={dispatch} employees={props.employees} />
      ) : null}

      {props.step === 4 ? (
        <TimeStep
          state={state}
          dispatch={dispatch}
          dayWindows={props.dayWindows}
          today={props.today}
          onPickDay={props.onPickDay}
          onPickWindow={props.onPickWindow}
        />
      ) : null}

      {props.step === 5 ? (
        <SummaryStep
          state={state}
          dispatch={dispatch}
          services={props.services}
          employees={props.employees}
          today={props.today}
          consentGrantedAt={props.consentGrantedAt}
          ready={props.ready}
          deniedReason={props.deniedReason}
          onSubmit={props.onSubmit}
          onClose={props.onClose}
        />
      ) : null}

      {props.step !== 5 && state.failure !== null ? (
        <div className={styles["failure"]} role="alert">
          {state.failure}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Шаг «Время»: свободные окна, а не пустой календарь (ТЗ §5.10, макет 5-А2).
 *
 * ПОЧЕМУ ОКНА, А НЕ ПОЛЕ ВРЕМЕНИ. Поле времени спрашивает «во сколько?», а у
 * администратора с трубкой в руке вопрос обратный: «что у вас есть?». Он всё равно
 * перебирает время вслепую, пока не попадёт в свободное, — и каждая неудачная попытка
 * это «ой, занято» в трубку. Окна отвечают сразу.
 *
 * РУЧНОЙ ВВОД ОСТАЁТСЯ. Без мастера окон не бывает — свободное время всегда чьё-то, —
 * и запись в колонку неразобранных делается временем, а не выбором окна.
 */
function TimeStep(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly dayWindows: BookingDayWindows | null
  readonly today: string
  readonly onPickDay: (date: string) => void
  readonly onPickWindow: (window: FreeWindow) => void
}): ReactNode {
  const draft = props.state.draft
  const shown = draft.date
  const tomorrow = shiftDate(props.today, 1)
  const noMaster = draft.employeeId === null

  /*
   * ОКНА СПРАШИВАЮТСЯ САМИ, КАК ТОЛЬКО ОТКРЫЛСЯ ШАГ.
   *
   * Иначе экран бессрочно показывал «Смотрим, что свободно…» — не загружалось
   * ничего, потому что окна запрашивал только чип дня, уже подсвеченный как
   * нажатый. Обещание «свободные окна, а не пустой календарь» не выполнялось,
   * пока человек не догадается нажать то, что и так выглядит нажатым.
   */
  const fresh =
    props.dayWindows !== null &&
    props.dayWindows.date === draft.date &&
    props.dayWindows.employeeId === (draft.employeeId ?? "") &&
    props.dayWindows.durationMinutes === draft.durationMinutes
  const { onPickDay } = props
  useEffect(() => {
    if (fresh) return
    onPickDay(draft.date)
  }, [fresh, draft.date, onPickDay])

  return (
    <>
      <div className={styles["dayChips"]} role="group" aria-label="День">
        {[
          { date: props.today, label: "Сегодня" },
          { date: tomorrow, label: "Завтра" },
        ].map((chip) => (
          <Button
            key={chip.date}
            size="sm"
            variant={shown === chip.date ? "primary" : "secondary"}
            aria-pressed={shown === chip.date}
            onClick={() => {
              props.onPickDay(chip.date)
            }}
          >
            {chip.label}
          </Button>
        ))}
        {/*
          * Третий чип — произвольная дата. Системный выбор даты здесь уместен: это
          * редкий случай («клиент просит через две недели»), и городить свой календарь
          * ради него значит спорить с тем, к чему человек привык на своём телефоне.
          */}
        <TextField
          label="Дата"
          type="date"
          value={shown}
          /* Записать в прошлое этим экраном нельзя: для этого есть правка записи
             с отдельным правом, а не форма создания. */
          min={props.today}
          onChange={(event) => {
            const value = event.currentTarget.value
            if (value.length === 10 && value >= props.today) props.onPickDay(value)
          }}
        />
      </div>

      {noMaster ? (
        <span className={styles["fieldHint"]}>
          Мастер не выбран, поэтому свободных окон нет: свободное время всегда чьё-то.
          Укажите время вручную — запись ляжет в колонку неразобранных.
        </span>
      ) : !fresh || props.dayWindows === null || props.dayWindows.status === "loading" ? (
        // Устаревшие окна не показываются вовсе: список, посчитанный под другого
        // мастера или другую длительность, — это предложение продать занятое время.
        <span className={styles["fieldHint"]}>Смотрим, что свободно…</span>
      ) : props.dayWindows.status === "failed" ? (
        <span className={styles["fieldHint"]}>
          Не удалось посмотреть свободное время. Укажите его вручную или повторите,
          выбрав день ещё раз.
        </span>
      ) : props.dayWindows.windows.length === 0 ? (
        <span className={styles["fieldHint"]}>
          В этот день у мастера свободного времени нет. Возьмите другой день или
          другого мастера — либо поставьте время вручную, если договорились иначе.
        </span>
      ) : (
        <>
          <span className={styles["fieldHint"]}>
            Свободные окна, а не пустой календарь:
            {props.dayWindows.truncated
              ? " показаны ближайшие, дальше по дню места ещё есть"
              : ""}
          </span>
          <div className={styles["windowGrid"]} role="group" aria-label="Свободное время">
            {props.dayWindows.windows.map((window) => {
              const on = window.date === draft.date && window.minute === draft.startMinute
              return (
                <button
                  key={`${window.date}-${String(window.minute)}`}
                  type="button"
                  className={on ? styles["windowChipOn"] : styles["windowChip"]}
                  aria-pressed={on}
                  onClick={() => {
                    props.onPickWindow(window)
                  }}
                >
                  {formatMinute(window.minute)}
                </button>
              )
            })}
          </div>
        </>
      )}

      <TextField
        label="Или вручную"
        type="time"
        step={300}
        value={formatMinute(draft.startMinute)}
        hint={formatRange(draft.startMinute, draft.startMinute + draft.durationMinutes)}
        onChange={(event) => {
          const minutes = parseTime(event.currentTarget.value)
          if (minutes === null) return
          props.dispatch({ type: "start-minute", minute: minutes })
        }}
      />
    </>
  )
}

/**
 * Шаг «Итог»: весь заказ одним взглядом перед тем, как он станет записью.
 *
 * ЭТО НЕ УКРАШЕНИЕ. Предыдущие четыре шага человек видел по одному, и подтверждать
 * он должен целое, а не память о нём: ошибка «записал к другому мастеру» ловится
 * здесь и нигде больше.
 */
function SummaryStep(props: {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly services: readonly BookableService[]
  readonly employees: readonly { readonly id: string; readonly displayName: string }[]
  readonly today: string
  readonly consentGrantedAt: string | null
  readonly ready: boolean
  readonly deniedReason: string | undefined
  readonly onSubmit: (notifyClient: boolean) => void
  readonly onClose: () => void
}): ReactNode {
  const { state } = props
  const draft = state.draft
  const master =
    draft.employeeId === null
      ? "Без мастера"
      : (props.employees.find((item) => item.id === draft.employeeId)?.displayName ?? "—")
  const price = totalPriceMinor(draft.serviceIds, props.services)
  const client =
    draft.clientId !== null && state.client.kind === "found"
      ? state.client.client.displayName
      : draft.clientName.trim() === ""
        ? "Новый клиент"
        : draft.clientName.trim()
  const when =
    draft.date === props.today
      ? `Сегодня, ${formatRange(draft.startMinute, draft.startMinute + draft.durationMinutes)}`
      : `${draft.date}, ${formatRange(draft.startMinute, draft.startMinute + draft.durationMinutes)}`

  return (
    <>
      <div className={styles["summaryRows"]}>
        {[
          { key: "Клиент", value: client },
          { key: "Услуги", value: servicesSummary(draft.serviceIds, props.services) ?? "—" },
          { key: "Мастер", value: master },
          { key: "Время", value: when },
          ...(price === null ? [] : [{ key: "Цена", value: formatMoney(price) }]),
        ].map((row) => (
          <div className={styles["summaryRow"]} key={row.key}>
            <span className={styles["summaryKey"]}>{row.key}</span>
            <span className={styles["summaryValue"]}>{row.value}</span>
          </div>
        ))}
      </div>

      {/*
        * ДАТА СОГЛАСИЯ — НЕ ФОРМАЛЬНОСТЬ. Она отвечает на вопрос, который задают
        * при проверке: на каком основании салон держит номер этого человека.
        */}
      {/*
        * ОБЕЩАТЬ ЗАПИСЬ СОГЛАСИЯ МОЖНО ТОЛЬКО НОВОМУ КЛИЕНТУ. Для существующего
        * согласие не отправляется вовсе — карточка уже есть, — и фраза «будет
        * записано» была бы неправдой ровно там, где её читают как гарантию.
        */}
      <span className={styles["summaryNote"]}>
        {props.consentGrantedAt !== null
          ? `Согласие на обработку данных получено ${props.consentGrantedAt}`
          : draft.clientId === null
            ? "Согласие на обработку данных запишется вместе с карточкой, со слов клиента."
            : "Дату согласия показать не удалось — она есть в карточке клиента."}
        {" · комментарий необязателен"}
      </span>

      <TextField
        label="Комментарий"
        placeholder="Необязательно"
        value={draft.comment}
        onChange={(event) => {
          props.dispatch({ type: "comment", value: event.currentTarget.value })
        }}
      />

      <WarningsBlock
        state={state}
        ready={props.ready}
        deniedReason={props.deniedReason}
        onConfirm={() => {
          props.onSubmit(false)
        }}
        onCancel={props.onClose}
      />

      {state.failure === null ? null : (
        <div className={styles["failure"]} role="alert">
          {state.failure}
        </div>
      )}
    </>
  )
}

/**
 * Подвал шагов: «Назад» и главное действие.
 *
 * ГЛАВНОЕ ДЕЙСТВИЕ НЕ ВРЁТ. Пока шаг не пройден, кнопка «Дальше» показывает ПРИЧИНУ,
 * а не просто гаснет: выключенная кнопка без объяснения — это тупик, из которого
 * человек выходит звонком администратору.
 */
function StepFoot(props: {
  readonly step: BookingStep
  readonly state: BookingState
  readonly ready: boolean
  readonly deniedReason: string | undefined
  readonly onStep: (step: BookingStep) => void
  readonly onSubmit: (notifyClient: boolean) => void
  readonly onClose: () => void
}): ReactNode {
  const last = props.step === BOOKING_STEP_COUNT
  const blocker = stepBlocker(props.state, props.step)
  const hasWarnings = props.state.warnings.length > 0
  const denied = props.deniedReason ?? blocker ?? undefined

  return (
    <footer className={styles["stepFoot"]}>
      <Button
        className={styles["stepBack"]}
        variant="secondary"
        onClick={() => {
          // С первого шага «Назад» закрывает форму: некуда — значит наружу, а не
          // в никуда. Введённое при этом остаётся, как и при Escape.
          if (props.step === 1) props.onClose()
          else props.onStep((props.step - 1) as BookingStep)
        }}
      >
        Назад
      </Button>
      <Button
        className={styles["stepNext"]}
        variant="primary"
        loading={last && props.state.submitting}
        loadingLabel="Записываем…"
        deniedReason={last && hasWarnings ? "Сначала решите предупреждение выше" : denied}
        onClick={() => {
          if (!last) {
            props.onStep((props.step + 1) as BookingStep)
            return
          }
          if (hasWarnings || !props.ready) return
          props.onSubmit(false)
        }}
      >
        {stepSubmitLabel(props.step, hasWarnings)}
      </Button>
    </footer>
  )
}
